import { useContext, useEffect, useState } from 'react';
import { MyCompanyProfileContext } from '../MyCompanyProfileContext';
import { UserApiContext } from '../Components/UserDetailsContext/UserDetailsContext';
import { toast } from 'react-toastify';

/**
 * Centralized Access Control Hook for View-Only Users
 * 
 * This hook checks the user's permissions and determines whether they have view-only access to certain modules.
 * 
 * @param {string} moduleKey - The key of the module to check access for (e.g., 'masters', 'spot', 'rfq_details').
 * @returns {Object} Access control properties and helper methods.
 */
const useViewOnlyAccess = (moduleKey, screenKey, allowByDefault = false) => {
    const { companySettingData } = useContext(MyCompanyProfileContext);
    const { userDetails } = useContext(UserApiContext);

    const [accessState, setAccessState] = useState({
        loading: true,
        canAccess: false,          // Now means Edit Access
        canAccessReports: false,   // Renamed from canViewReports
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        hasModuleAccess: false,
        dashboardAccess: false,    // New: Specific dashboard access
        noAccess: false,           // New: Explicit no access flag
        isRestricted: true,
        userFound: false,
    });

    useEffect(() => {
        const restrictedState = {
            loading: false,
            canAccess: false,
            canAccessReports: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
            hasModuleAccess: false,
            dashboardAccess: false,
            noAccess: false,
            isRestricted: true,
            userFound: false,
        };

        const fullAccessState = {
            loading: false,
            canAccess: true,
            canAccessReports: true,
            canCreate: true,
            canUpdate: true,
            canDelete: true,
            hasModuleAccess: true,
            dashboardAccess: true,
            noAccess: false,
            isRestricted: false,
            userFound: true,
        };

        if (!companySettingData || !companySettingData.contact_information) {
            if (!companySettingData) {
                setAccessState(prev => ({ ...prev, loading: true }));
            } else {
                setAccessState(restrictedState);
            }
            return;
        }

        try {
            const email = userDetails?.email?.toLowerCase();

            if (!email) {
                setAccessState(restrictedState);
                return;
            }

            const user = companySettingData.contact_information.find(
                u => u.email?.toLowerCase() === email
            );

            if (!user) {
                setAccessState(restrictedState);
                return;
            }

            // If access_control doesn't exist
            if (!user.access_control) {
                // Modules that require Super Admin access
                const SUPER_ADMIN_MODULES = [
                    'general_info',
                    'organization_team',
                    'masters',
                    'terms_conditions',
                    'operations_overview',
                    'my_transporters',
                    'settings',
                    'rfq_details',
                    'report_generator'
                ];

                if (SUPER_ADMIN_MODULES.includes(moduleKey)) {
                    if (user.super_admin === true) {
                        setAccessState(fullAccessState);
                    } else {
                        // Non-Super Admins are blocked from these modules
                        setAccessState({
                            ...restrictedState,
                            loading: false,
                            userFound: true,
                            hasModuleAccess: false,
                        });
                    }
                } else {
                    // For other modules, default to full edit access for everyone
                    setAccessState(fullAccessState);
                }
                return;
            }

            // Look for specific module/screen permission
            const modulePermission = user.access_control.modules?.find(
                m => m.module_key === moduleKey && (!screenKey || m.screen === screenKey)
            );

            if (modulePermission) {
                // If it exists in access_control, follow the defined permission regardless of super_admin status
                // can_access === true means Edit, can_access === false means View Only
                const editable = !!modulePermission.can_access;
                const canReport = !!modulePermission.can_access_reports;
                const dashboardAccess = !!modulePermission.dashboard_access;
                const noAccess = !!modulePermission.no_access;

                // Determine effective module access
                // If no_access is true, block access unless dashboard_access is specifically granted
                const hasEffectiveAccess = noAccess ? dashboardAccess : true;

                setAccessState({
                    loading: false,
                    canAccess: true, // User can "see" the module
                    canAccessReports: canReport,
                    canCreate: editable,
                    canUpdate: editable,
                    canDelete: editable,
                    hasModuleAccess: hasEffectiveAccess,
                    dashboardAccess: dashboardAccess,
                    noAccess: noAccess,
                    isRestricted: !editable,
                    userFound: true,
                });
            } else {
                // Module not found in access_control list
                // If user is super admin or allowByDefault is true, they have access by default to unlisted modules
                if (user.super_admin === true || allowByDefault || moduleKey === 'report_generator') {
                    setAccessState(fullAccessState);
                } else {
                    // Regular users with access_control only see what is specifically granted
                    setAccessState({
                        ...restrictedState,
                        loading: false,
                        userFound: true,
                        hasModuleAccess: false,
                    });
                }
            }
        } catch (error) {
            console.error('Error checking access control:', error);
            setAccessState(restrictedState);
        }
    }, [companySettingData, moduleKey, screenKey, userDetails, allowByDefault]);

    /**
     * Check if user has permission for specific actions (Create, Update, Delete)
     */
    const checkActionPermission = (actionType, actionCallback, customMessage) => {
        let actualActionType = actionType;
        let actualCallback = actionCallback;
        let actualMessage = customMessage;

        if (typeof actionType === 'function') {
            actualActionType = 'update';
            actualCallback = actionType;
            actualMessage = actionCallback;
        }

        let canPerformAction = false;

        switch (actualActionType) {
            case 'create':
                canPerformAction = accessState.canCreate;
                break;
            case 'update':
                canPerformAction = accessState.canUpdate;
                break;
            case 'delete':
                canPerformAction = accessState.canDelete;
                break;
            case 'view':
                canPerformAction = accessState.hasModuleAccess;
                break;
            case 'report':
                canPerformAction = accessState.canAccessReports;
                break;
            default:
                canPerformAction = false;
        }

        if (!canPerformAction) {
            toast.info(actualMessage || "You don't have permission to perform this action.");
            return false;
        }

        if (actualCallback && typeof actualCallback === 'function') {
            actualCallback();
        }
        return true;
    };

    /**
     * Get disabled state and tooltip for buttons/actions.
     */
    const getRestrictionProps = (customTooltip) => {
        return {
            disabled: accessState.isRestricted,
            title: accessState.isRestricted ? (customTooltip || "Access Restricted") : "",
            style: accessState.isRestricted
                ? { cursor: 'not-allowed', opacity: 0.6 }
                : { cursor: 'pointer' }
        };
    };

    const handleRestrictedClick = (customMessage) => {
        if (accessState.isRestricted) {
            toast.info(customMessage || "You don't have permission to perform this action.");
        }
    };

    return {
        ...accessState,
        checkActionPermission,
        getRestrictionProps,
        handleRestrictedClick
    };
};

export default useViewOnlyAccess;

